import {
  DEFAULT_MODEL_PROVIDERS,
  type InstanceModelConnection,
  type ModelAccount,
  type ModelAccountBinding,
  type ModelConnectionSetup,
  type ModelConnectionsOverview,
  type ModelHealthUnknownReason,
  type ModelProviders,
  modelHealthUnknownReasonSchema,
  modelProvidersSchema,
  type OperatorLabView,
  type OperatorTenant,
  PROVIDER_KIND_IDS,
  PROVIDER_KINDS,
  type ProviderKind,
  providerAllowed,
  type RetentionMode,
  settingLoosens,
} from "@esi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import {
  ArrowDown,
  ArrowUp,
  Building2,
  CircleAlert,
  CircleCheck,
  CircleDashed,
  CircleHelp,
  CircleX,
  Clock,
  Cloud,
  Ellipsis,
  type LucideIcon,
  Plus,
  ShieldAlert,
} from "lucide-react";
import { type ReactNode, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { NativeSelect } from "@/components/NativeSelect";
import { Notice } from "@/components/Notice";
import { Page } from "@/components/Page";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { api } from "../api.ts";
import { ApiError, apiError, needsReauth, ok } from "../api-errors.ts";
import { StepUpForm, StepUpFrame, useSecureAction } from "../security/step-up.tsx";
import { PageSection } from "../settings-shell/PageSection.tsx";
import { useCsrfHeaders } from "./operator.tsx";
import { useOperatorStepUp } from "./operator-step-up.ts";
import { consoleKeys, usePolicies, useTenants } from "./queries.ts";

/**
 * Model connections (openspec change `model-connections-cross-account`): Bedrock in other AWS
 * accounts, registered at runtime. The providers the instance allows (model-picking D1), the labs
 * and their state (Block, Unblock; slice 4c), the
 * kill switch, then the connections in route order (`default`, this deployment's own account,
 * always last), then the account allowlist, whose additions and bindings (model-picking D2) need
 * a fresh authenticator code. Status is icon and word, never colour alone.
 */

const KILL_SWITCH = "models.crossAccountConnections";
const HEALTH_INTERVAL = "models.healthProbeInterval";
const PROVIDERS = "models.providers";
const operator = api.api.operator;

/** While a run is due: the updater runs within seconds of a change. */
export const PERIMETER_PENDING_POLL_MS = 5_000;
/** Once overdue: the updater retries every 15 minutes, the page follows it at a slower pace. */
export const PERIMETER_OVERDUE_POLL_MS = 60_000;

/**
 * How often the overview refetches: the perimeter updater runs after the change's own
 * invalidation, so the page polls until the perimeter follows the allowlist (or stays overdue),
 * and the applied state or the warning shows without a reload.
 */
export function perimeterRefetchInterval(
  perimeter: ModelConnectionsOverview["perimeter"] | undefined,
): number | false {
  if (!perimeter) {
    return false;
  }
  if (perimeter.overdue) {
    return PERIMETER_OVERDUE_POLL_MS;
  }
  const pending =
    perimeter.diverged || perimeter.pendingAdds.length > 0 || perimeter.pendingRemovals.length > 0;
  return pending ? PERIMETER_PENDING_POLL_MS : false;
}

function useOverview() {
  return useQuery({
    queryKey: consoleKeys.modelConnections,
    queryFn: async (): Promise<ModelConnectionsOverview> =>
      ok(await operator["model-connections"].$get()),
    retry: false,
    refetchInterval: (query) => perimeterRefetchInterval(query.state.data?.perimeter),
  });
}

/**
 * The connections the health probe cannot reach (#423), with the reason: their stored health
 * is `unknown` (fresh rows only). Read from the operators' health rows (`/operator/model-health`).
 */
export function unreachableConnections(
  rows: readonly { connectionId: string; status: string; reason: string | null; stale: boolean }[],
): Map<string, ModelHealthUnknownReason> {
  const out = new Map<string, ModelHealthUnknownReason>();
  for (const r of rows) {
    const reason = modelHealthUnknownReasonSchema.safeParse(r.reason);
    if (r.status === "unknown" && !r.stale && reason.success && !out.has(r.connectionId)) {
      out.set(r.connectionId, reason.data);
    }
  }
  return out;
}

/**
 * The connections whose role refuses the list calls (`list_denied`, slice 4b): they keep serving
 * the versions that answered there but adopt nothing new. Read from the operators' discovery rows.
 */
export function listDeniedConnections(
  discovery: readonly { connectionId: string; status: string }[],
): Set<string> {
  return new Set(discovery.filter((d) => d.status === "list_denied").map((d) => d.connectionId));
}

function useConnectionHealth() {
  return useQuery({
    queryKey: consoleKeys.modelHealth,
    queryFn: async () => {
      const view = await ok(await operator["model-health"].$get());
      return {
        unreachable: unreachableConnections(view.rows),
        // The previous release's API (N-1) has no discovery rows.
        listDenied: listDeniedConnections(view.discovery ?? []),
      };
    },
    retry: false,
  });
}

/** Why a connection can't list its models (`list_denied`): the two actions its role needs. */
function ListDeniedNote() {
  const { t } = useTranslation();
  return (
    <p className="flex items-start gap-1.5 text-xs text-fg-secondary">
      <CircleHelp
        aria-hidden="true"
        className="mt-px size-3.5 shrink-0 text-warning-fg"
        strokeWidth={1.75}
      />
      <span>{t("console.models.health.listDenied")}</span>
    </p>
  );
}

/** The page's own refusals, in words (the API's codes). */
function refusal(error: Error, t: TFunction) {
  if (error instanceof ApiError && error.code) {
    const known: Record<string, string> = {
      model_connection_probe_required: t("console.models.refused.probe"),
      model_connection_logging_confirm: t("console.models.refused.logging"),
      model_connection_same_account: t("console.models.refused.sameAccount"),
      model_account_same_account: t("console.models.refused.sameAccount"),
      model_account_capacity: t("console.models.refused.capacity"),
      model_provider_disabled: t("console.models.refused.providerOff"),
      model_account_tenant_bound: t("console.models.refused.tenantBound"),
      model_account_same_binding: t("console.models.refused.sameBinding"),
      model_account_tenant_unavailable: t("console.models.refused.tenantUnavailable"),
    };
    if (known[error.code]) {
      return known[error.code];
    }
    if (error.status === 409) {
      return error.message;
    }
  }
  return error.message;
}

export function ModelConnectionsPage() {
  const { t } = useTranslation();
  const overview = useOverview();
  return (
    <Page>
      <PageHeader title={t("console.models.title")} lead={t("console.models.lead")} />
      {overview.isError ? (
        <Notice tone="danger">{overview.error.message}</Notice>
      ) : !overview.data ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <div className="flex flex-col divide-y divide-line">
          <PerimeterNotice perimeter={overview.data.perimeter} />
          <Providers />
          <Labs />
          <KillSwitch />
          <HealthInterval />
          <Connections overview={overview.data} />
          <Allowlist overview={overview.data} />
        </div>
      )}
    </Page>
  );
}

// ── Perimeter state ─────────────────────────────────────────────────────────────────────────

/**
 * Shown when the perimeter has not followed the allowlist for longer than a run takes, or its
 * last update failed (a lost or failed updater run). The updater retries every 15 minutes.
 */
function PerimeterNotice({ perimeter }: { perimeter: ModelConnectionsOverview["perimeter"] }) {
  const { t } = useTranslation();
  if (!perimeter.overdue) {
    return null;
  }
  return (
    <div className="pb-6">
      <Notice tone="warning" live title={t("console.models.perimeter.title")}>
        <ul className="flex flex-col gap-1">
          {perimeter.error ? (
            <li>{t(`console.models.perimeter.error.${perimeter.error}`)}</li>
          ) : null}
          {perimeter.pendingRemovals.length > 0 ? (
            <li>
              {t("console.models.perimeter.removals", {
                accounts: perimeter.pendingRemovals.join(", "),
              })}
            </li>
          ) : null}
          {perimeter.pendingAdds.length > 0 ? (
            <li>
              {t("console.models.perimeter.adds", { accounts: perimeter.pendingAdds.join(", ") })}
            </li>
          ) : null}
          <li>{t("console.models.perimeter.retry")}</li>
        </ul>
      </Notice>
    </div>
  );
}

// ── Providers ───────────────────────────────────────────────────────────────────────────────

type Owner = "instance" | "tenant";

/**
 * `models.providers` (model-picking D1): per provider kind, whether the instance's connections
 * may use it and whether tenants may register their own. Kinds without an adapter are listed,
 * not yet available. Turning one on widens what may call models: it asks first.
 */
/** The instance's `models.providers` (the default until the policies load). */
function useProviders() {
  const policies = usePolicies();
  const policy = policies.data?.find((p) => p.key === PROVIDERS);
  const parsed = modelProvidersSchema.safeParse(policy?.effectiveValue);
  const current: ModelProviders = parsed.success ? parsed.data : DEFAULT_MODEL_PROVIDERS;
  return { policy, current };
}

function Providers() {
  const { t } = useTranslation();
  const headers = useCsrfHeaders();
  const queryClient = useQueryClient();
  const { policy, current } = useProviders();
  const [asking, setAsking] = useState<{ kind: ProviderKind; owner: Owner } | null>(null);
  const save = useMutation({
    mutationFn: async (value: ModelProviders) =>
      ok(
        await operator.policies[":key"].$put(
          { param: { key: PROVIDERS }, json: { value, locked: policy?.locked ?? false } },
          headers,
        ),
      ),
    onSettled: () => queryClient.invalidateQueries({ queryKey: consoleKeys.policies }),
  });
  const next = (kind: ProviderKind, owner: Owner, on: boolean): ModelProviders => ({
    ...current,
    [kind]: {
      instance: providerAllowed(current, kind, "instance"),
      tenant: providerAllowed(current, kind, "tenant"),
      [owner]: on,
    },
  });
  const toggle = (kind: ProviderKind, owner: Owner, on: boolean) => {
    const value = next(kind, owner, on);
    if (settingLoosens(PROVIDERS, current, value)) {
      setAsking({ kind, owner });
      return;
    }
    save.mutate(value);
  };
  return (
    <PageSection id="model-providers" title={t("console.models.providers.title")}>
      <p className="max-w-prose text-sm text-fg-secondary">{t("console.models.providers.lead")}</p>
      <table className="w-full border-separate border-spacing-0 overflow-hidden rounded-xl border border-line bg-surface-raised text-sm">
        <thead>
          <tr className="text-left text-xs text-fg-muted">
            <th scope="col" className="px-4 py-2 font-medium">
              {t("console.models.providers.kind")}
            </th>
            <th scope="col" className="w-28 px-4 py-2 text-center font-medium">
              {t("console.models.providers.instance")}
            </th>
            <th scope="col" className="w-28 px-4 py-2 text-center font-medium">
              {t("console.models.providers.tenant")}
            </th>
          </tr>
        </thead>
        <tbody>
          {PROVIDER_KIND_IDS.map((kind) => {
            const info = PROVIDER_KINDS[kind];
            return (
              <tr key={kind} className="[&>*]:border-t [&>*]:border-line">
                <th scope="row" className="px-4 py-3 text-left font-normal">
                  <span className="flex flex-col gap-0.5">
                    <span className="font-medium text-fg">{info.displayName}</span>
                    {info.adapter ? null : (
                      <span className="text-xs text-fg-muted">
                        {t("console.models.providers.notYet")}
                      </span>
                    )}
                  </span>
                </th>
                {(["instance", "tenant"] as const).map((owner) => (
                  <td key={owner} className="px-4 py-3 text-center">
                    <Switch
                      aria-label={t(`console.models.providers.${owner}Of`, {
                        name: info.displayName,
                      })}
                      checked={providerAllowed(current, kind, owner)}
                      disabled={!info.adapter || !policy || save.isPending}
                      onCheckedChange={(on) => toggle(kind, owner, on)}
                    />
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="text-xs text-fg-muted">
        {providerAllowed(current, "bedrock", "instance")
          ? t("console.models.providers.tenantLater")
          : t("console.models.providers.bedrockOff")}
      </p>
      {asking ? (
        <Notice tone="warning" live title={t("console.models.providers.confirmTitle")}>
          <p>
            {t(`console.models.providers.confirm.${asking.owner}`, {
              name: PROVIDER_KINDS[asking.kind].displayName,
            })}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={() => {
                save.mutate(next(asking.kind, asking.owner, true));
                setAsking(null);
              }}
            >
              {t("console.models.providers.turnOn")}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setAsking(null)}>
              {t("app.cancel")}
            </Button>
          </div>
        </Notice>
      ) : null}
      {save.error ? <Notice tone="danger">{refusal(save.error, t)}</Notice> : null}
    </PageSection>
  );
}

// ── Labs ────────────────────────────────────────────────────────────────────────────────────

const LABS = "models.labs";

/**
 * Bedrock's labs (model-picking D1, D17, slice 4c): one row per lab the stage grants, with its
 * state (Blocked; Available, not tested by ESI; Tested by ESI with the badge), where it has
 * versions, how many tenants opted in, and a Block or Unblock. Unblocking offers the lab to
 * tenants again: it asks first. Every tenant's default lab cannot be blocked.
 */
function Labs() {
  const { t } = useTranslation();
  const headers = useCsrfHeaders();
  const queryClient = useQueryClient();
  const policies = usePolicies();
  const policy = policies.data?.find((p) => p.key === LABS);
  const labs = useQuery({
    queryKey: consoleKeys.modelLabs,
    queryFn: async (): Promise<OperatorLabView[]> =>
      (await ok(await operator["model-labs"].$get())).labs,
    retry: false,
  });
  const [asking, setAsking] = useState<OperatorLabView | null>(null);
  const save = useMutation({
    mutationFn: async (value: string[]) =>
      ok(
        await operator.policies[":key"].$put(
          { param: { key: LABS }, json: { value, locked: policy?.locked ?? false } },
          headers,
        ),
      ),
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: consoleKeys.policies }),
        queryClient.invalidateQueries({ queryKey: consoleKeys.modelLabs }),
      ]),
  });
  const rows = labs.data ?? [];
  const current = rows.filter((l) => l.state !== "blocked").map((l) => l.lab);
  const listWith = (lab: string, on: boolean) =>
    rows.map((l) => l.lab).filter((id) => (id === lab ? on : current.includes(id)));
  return (
    <PageSection id="model-labs" title={t("console.models.labs.title")}>
      <p className="max-w-prose text-sm text-fg-secondary">{t("console.models.labs.lead")}</p>
      {labs.isError ? (
        <Notice tone="danger">{labs.error.message}</Notice>
      ) : !labs.data ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <ul className="divide-y divide-line overflow-hidden rounded-xl border border-line bg-surface-raised">
          {rows.map((lab) => (
            <li key={lab.lab} className="flex flex-wrap items-start gap-x-4 gap-y-2 px-4 py-3">
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-fg">{lab.displayName}</span>
                  <LabState lab={lab} />
                </span>
                <span className="text-xs text-fg-secondary">
                  {lab.geographies.includes("eu")
                    ? t("console.models.labs.geographies", {
                        list: lab.geographies
                          .map((g) =>
                            g === "eu"
                              ? t("console.models.labs.geo.eu")
                              : t("console.models.labs.geo.global"),
                          )
                          .join(" · "),
                      })
                    : t("console.models.labs.notInEu")}
                  {" · "}
                  {lab.defaultLab
                    ? t("console.models.labs.default")
                    : lab.tenants === null
                      ? null
                      : t("console.models.labs.tenants", { count: lab.tenants })}
                </span>
                {lab.accessDenied ? (
                  <span className="flex items-start gap-1.5 text-xs text-fg-secondary">
                    <CircleHelp
                      aria-hidden="true"
                      className="mt-px size-3.5 shrink-0 text-warning-fg"
                      strokeWidth={1.75}
                    />
                    <span>{t("console.models.labs.accessDenied")}</span>
                  </span>
                ) : null}
              </div>
              {lab.defaultLab ? null : lab.state === "blocked" ? (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!policy || save.isPending}
                  onClick={() => setAsking(lab)}
                >
                  {t("console.models.labs.unblock")}
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={!policy || save.isPending}
                  aria-label={t("console.models.labs.blockOf", { name: lab.displayName })}
                  onClick={() => save.mutate(listWith(lab.lab, false))}
                >
                  {t("console.models.labs.block")}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {asking ? (
        <Notice tone="warning" live title={t("console.models.labs.confirmTitle")}>
          <p>{t("console.models.labs.confirm", { name: asking.displayName })}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={() => {
                // An unblock always loosens (`settingLoosens`): it is only sent once confirmed.
                save.mutate(listWith(asking.lab, true));
                setAsking(null);
              }}
            >
              {t("console.models.labs.unblock")}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setAsking(null)}>
              {t("app.cancel")}
            </Button>
          </div>
        </Notice>
      ) : null}
      {save.error ? <Notice tone="danger">{refusal(save.error, t)}</Notice> : null}
    </PageSection>
  );
}

/** A lab's state, in words with an icon (never colour alone), the badge when tested. */
function LabState({ lab }: { lab: OperatorLabView }) {
  const { t } = useTranslation();
  if (lab.state === "blocked") {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-fg-muted">
        <CircleX aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
        {t("console.models.labs.state.blocked")}
      </span>
    );
  }
  if (lab.state === "tested" && lab.evaluation) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-line px-2 py-0.5 text-xs text-fg">
        <CircleCheck aria-hidden="true" className="size-3.5 text-success-fg" strokeWidth={1.75} />
        {t("console.models.labs.state.tested", {
          date: lab.evaluation.date,
          version: lab.evaluation.catalogueVersion,
        })}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-fg-secondary">
      <CircleDashed aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
      {t("console.models.labs.state.untested")}
    </span>
  );
}

// ── Kill switch ─────────────────────────────────────────────────────────────────────────────

function KillSwitch() {
  const { t } = useTranslation();
  const id = useId();
  const headers = useCsrfHeaders();
  const queryClient = useQueryClient();
  const policies = usePolicies();
  const policy = policies.data?.find((p) => p.key === KILL_SWITCH);
  const on = (policy?.effectiveValue ?? true) === true;
  const save = useMutation({
    mutationFn: async (value: boolean) =>
      ok(
        await operator.policies[":key"].$put(
          { param: { key: KILL_SWITCH }, json: { value, locked: policy?.locked ?? false } },
          headers,
        ),
      ),
    onSettled: () => queryClient.invalidateQueries({ queryKey: consoleKeys.policies }),
  });
  return (
    <section aria-labelledby={`${id}-label`} className="flex flex-col gap-3 py-8 first:pt-0">
      <div className="flex items-start justify-between gap-4 rounded-xl border border-line bg-surface-raised p-4">
        <div className="flex min-w-0 flex-col gap-1">
          <h2 id={`${id}-label`} className="text-base font-semibold text-fg">
            {t("console.models.switch.title")}
          </h2>
          <p id={`${id}-help`} className="text-sm text-fg-secondary">
            {on ? t("console.models.switch.on") : t("console.models.switch.off")}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2 pt-0.5">
          <span className="text-sm text-fg-secondary">
            {on ? t("console.models.switch.onWord") : t("console.models.switch.offWord")}
          </span>
          <Switch
            aria-labelledby={`${id}-label`}
            aria-describedby={`${id}-help`}
            checked={on}
            disabled={!policy || save.isPending}
            onCheckedChange={(next) => save.mutate(next)}
          />
        </div>
      </div>
      {save.error ? <Notice tone="danger">{refusal(save.error, t)}</Notice> : null}
    </section>
  );
}

// ── Health checks ───────────────────────────────────────────────────────────────────────────

/**
 * How often ESI checks that each model answers on each connection (model-picking D6, Q1):
 * hourly by default, or daily; no deploy, the next 15-minute tick applies it.
 */
function HealthInterval() {
  const { t } = useTranslation();
  const id = useId();
  const headers = useCsrfHeaders();
  const queryClient = useQueryClient();
  const policies = usePolicies();
  const policy = policies.data?.find((p) => p.key === HEALTH_INTERVAL);
  const value = policy?.effectiveValue === "daily" ? "daily" : "hourly";
  const save = useMutation({
    mutationFn: async (next: "hourly" | "daily") =>
      ok(
        await operator.policies[":key"].$put(
          {
            param: { key: HEALTH_INTERVAL },
            json: { value: next, locked: policy?.locked ?? false },
          },
          headers,
        ),
      ),
    onSettled: () => queryClient.invalidateQueries({ queryKey: consoleKeys.policies }),
  });
  return (
    <section aria-labelledby={`${id}-label`} className="flex flex-col gap-3 py-8 first:pt-0">
      <div className="flex items-start justify-between gap-4 rounded-xl border border-line bg-surface-raised p-4">
        <div className="flex min-w-0 flex-col gap-1">
          <h2 id={`${id}-label`} className="text-base font-semibold text-fg">
            {t("console.models.health.title")}
          </h2>
          <p id={`${id}-help`} className="text-sm text-fg-secondary">
            {t("console.models.health.help")}
          </p>
        </div>
        <div className="shrink-0 pt-0.5">
          <NativeSelect
            aria-labelledby={`${id}-label`}
            aria-describedby={`${id}-help`}
            value={value}
            disabled={!policy || save.isPending}
            onChange={(e) => save.mutate(e.currentTarget.value === "daily" ? "daily" : "hourly")}
          >
            <option value="hourly">{t("console.models.health.hourly")}</option>
            <option value="daily">{t("console.models.health.daily")}</option>
          </NativeSelect>
        </div>
      </div>
      {save.error ? <Notice tone="danger">{refusal(save.error, t)}</Notice> : null}
    </section>
  );
}

// ── Connections ─────────────────────────────────────────────────────────────────────────────

type Status = { icon: LucideIcon; tone: string; text: string };

function connectionStatus(c: InstanceModelConnection, t: TFunction, bedrockOff = false): Status {
  if (c.accountTenantBound) {
    return {
      icon: ShieldAlert,
      tone: "text-warning-fg",
      text: t("console.models.status.tenantBound"),
    };
  }
  if (!c.accountApplied) {
    return { icon: Clock, tone: "text-warning-fg", text: t("console.models.status.notApplied") };
  }
  if (c.enabled && bedrockOff) {
    // `models.providers` turns Bedrock off for the instance: enabled, but out of routing.
    return {
      icon: CircleDashed,
      tone: "text-fg-muted",
      text: t("console.models.status.providerOff"),
    };
  }
  if (c.enabled) {
    return { icon: CircleCheck, tone: "text-success-fg", text: t("console.models.status.routing") };
  }
  switch (c.probeStatus) {
    case "ok":
      return c.probeFresh
        ? { icon: CircleCheck, tone: "text-fg-secondary", text: t("console.models.status.ready") }
        : { icon: CircleDashed, tone: "text-fg-muted", text: t("console.models.status.stale") };
    case "assume_denied":
      return {
        icon: CircleX,
        tone: "text-danger-fg",
        text: t("console.models.status.assumeDenied"),
      };
    case "model_denied":
      return {
        icon: CircleX,
        tone: "text-danger-fg",
        text: t("console.models.status.modelDenied"),
      };
    case "not_applied":
      return { icon: Clock, tone: "text-warning-fg", text: t("console.models.status.notApplied") };
    case "error":
      return { icon: CircleAlert, tone: "text-danger-fg", text: t("console.models.status.error") };
    default:
      return {
        icon: CircleDashed,
        tone: "text-fg-muted",
        text: t("console.models.status.unprobed"),
      };
  }
}

function StatusLine({ status }: { status: Status }) {
  const Icon = status.icon;
  return (
    <span className="inline-flex items-center gap-1.5 text-fg-secondary">
      <Icon aria-hidden="true" className={cn("size-3.5", status.tone)} strokeWidth={1.75} />
      {status.text}
    </span>
  );
}

function Connections({ overview }: { overview: ModelConnectionsOverview }) {
  const { t } = useTranslation();
  const [creating, setCreating] = useState(false);
  const [setup, setSetup] = useState<{ id: string; setup: ModelConnectionSetup } | null>(null);
  const connections = overview.connections;
  const health = useConnectionHealth().data;
  return (
    <PageSection id="model-connections" title={t("console.models.connections.title")}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-prose text-sm text-fg-secondary">
          {t("console.models.connections.lead")}
        </p>
        <Button
          onClick={() => setCreating(true)}
          disabled={overview.accounts.length === 0}
          title={
            overview.accounts.length === 0 ? t("console.models.connections.needAccount") : undefined
          }
        >
          <Plus aria-hidden="true" />
          {t("console.models.connections.add")}
        </Button>
      </div>
      <ol
        aria-label={t("console.models.connections.order")}
        className="divide-y divide-line rounded-xl border border-line bg-surface-raised"
      >
        {connections.map((c, i) => (
          <ConnectionRow
            key={c.id}
            connection={c}
            index={i}
            previous={connections[i - 1]}
            next={connections[i + 1]}
            healthUnknown={health?.unreachable.get(c.id)}
            listDenied={health?.listDenied.has(c.id) ?? false}
            onSetup={(s) => setSetup({ id: c.id, setup: s })}
          />
        ))}
        <li className="flex flex-col gap-1 px-4 py-3">
          <div className="flex items-center gap-2">
            <Cloud aria-hidden="true" className="size-4 text-fg-muted" strokeWidth={1.75} />
            <span className="text-sm font-medium text-fg">{t("console.models.default.title")}</span>
          </div>
          <p className="text-xs text-fg-muted">
            {t("console.models.default.body", {
              account: overview.deploymentAccount ?? t("console.models.default.thisAccount"),
            })}
          </p>
          {/* The deployment account's own connection (`default` in core). */}
          {health?.listDenied.has("default") ? <ListDeniedNote /> : null}
        </li>
      </ol>
      {creating ? (
        <CreateDialog
          accounts={overview.accounts}
          onClose={() => setCreating(false)}
          onCreated={(id, s) => {
            setCreating(false);
            setSetup({ id, setup: s });
          }}
        />
      ) : null}
      {setup ? <SetupDialog {...setup} onClose={() => setSetup(null)} /> : null}
    </PageSection>
  );
}

function ConnectionRow(props: {
  connection: InstanceModelConnection;
  /** Its index in the route order. */
  index: number;
  previous?: InstanceModelConnection;
  next?: InstanceModelConnection;
  /** The health probe cannot reach it (#423): why. */
  healthUnknown?: ModelHealthUnknownReason | undefined;
  /** Its role refuses the list calls (`list_denied`). */
  listDenied: boolean;
  onSetup: (setup: ModelConnectionSetup) => void;
}) {
  const { t } = useTranslation();
  const c = props.connection;
  const bedrockOff = !providerAllowed(useProviders().current, "bedrock", "instance");
  const headers = useCsrfHeaders();
  const queryClient = useQueryClient();
  const [confirmLogging, setConfirmLogging] = useState(false);
  // The connection's health line follows too (#423): a trust fixed then probed clears it.
  const settle = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: consoleKeys.modelConnections }),
      queryClient.invalidateQueries({ queryKey: consoleKeys.modelHealth }),
    ]);
  const patch = useMutation({
    mutationFn: async (json: { enabled?: boolean; position?: number; confirmLogging?: boolean }) =>
      ok(await operator["model-connections"][":id"].$patch({ param: { id: c.id }, json }, headers)),
    onSettled: settle,
  });
  const probe = useMutation({
    mutationFn: async () =>
      ok(await operator["model-connections"][":id"].probe.$post({ param: { id: c.id } }, headers)),
    onSettled: settle,
  });
  const setup = useMutation({
    mutationFn: async () =>
      ok(await operator["model-connections"][":id"].setup.$get({ param: { id: c.id } })),
    onSuccess: props.onSetup,
  });
  const remove = useMutation({
    mutationFn: async () => {
      const res = await operator["model-connections"][":id"].$delete(
        { param: { id: c.id } },
        headers,
      );
      if (!res.ok) {
        throw await apiError(res);
      }
    },
    onSettled: settle,
  });
  // Swapping positions with a neighbour: two writes, the route order is the sorted positions.
  // One write: the server puts the connection at that index of the route order and renumbers.
  const move = (index: number) => patch.mutate({ position: index });
  const toggle = (on: boolean) => {
    // Logging on, or unreadable: the server needs the operator's confirmation.
    const needsConfirm = c.loggingStatus !== "off";
    if (on && needsConfirm && !confirmLogging) {
      setConfirmLogging(true);
      return;
    }
    patch.mutate({ enabled: on, ...(on && needsConfirm && { confirmLogging: true }) });
    setConfirmLogging(false);
  };
  const error = patch.error ?? probe.error ?? setup.error ?? remove.error;
  const canEnable = c.enabled || (c.probeFresh && c.accountApplied);
  const switchId = useId();
  return (
    <li className="flex flex-col gap-2 px-4 py-3">
      <div className="flex items-start gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <label htmlFor={switchId} className="truncate text-sm font-medium text-fg">
            {c.label}
          </label>
          <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-fg-muted">
            <span className="font-mono">{c.id}</span>
            <span aria-hidden="true">·</span>
            <span className="tabular-nums">{c.accountId}</span>
            <span aria-hidden="true">·</span>
            <span>{t(`console.models.retention.${c.retention}`)}</span>
          </p>
          <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
            <StatusLine status={connectionStatus(c, t, bedrockOff)} />
            {c.loggingStatus === "on" ? (
              <span className="inline-flex items-center gap-1.5 text-fg-secondary">
                <ShieldAlert
                  aria-hidden="true"
                  className="size-3.5 text-warning-fg"
                  strokeWidth={1.75}
                />
                {t("console.models.logging.on")}
              </span>
            ) : c.loggingStatus === "off" ? (
              <span className="text-fg-muted">{t("console.models.logging.off")}</span>
            ) : c.loggingStatus === "unknown" && c.probeStatus === "ok" ? (
              <span className="text-fg-muted">{t("console.models.logging.unknown")}</span>
            ) : null}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Switch
            id={switchId}
            checked={c.enabled}
            disabled={!canEnable || patch.isPending}
            onCheckedChange={toggle}
            aria-describedby={canEnable ? undefined : `${switchId}-why`}
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t("console.models.actionsFor", { name: c.label })}
              >
                <Ellipsis aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => probe.mutate()} disabled={probe.isPending}>
                {t("console.models.probe")}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setup.mutate()}>
                {t("console.models.showSetup")}
              </DropdownMenuItem>
              <DropdownMenuItem disabled={!props.previous} onSelect={() => move(props.index - 1)}>
                <ArrowUp aria-hidden="true" />
                {t("console.models.moveUp")}
              </DropdownMenuItem>
              <DropdownMenuItem disabled={!props.next} onSelect={() => move(props.index + 1)}>
                <ArrowDown aria-hidden="true" />
                {t("console.models.moveDown")}
              </DropdownMenuItem>
              <DropdownMenuItem className="text-danger-fg" onSelect={() => remove.mutate()}>
                {t("console.models.delete")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      {props.healthUnknown ? (
        <p className="flex items-start gap-1.5 text-xs text-fg-secondary">
          <CircleHelp
            aria-hidden="true"
            className="mt-px size-3.5 shrink-0 text-warning-fg"
            strokeWidth={1.75}
          />
          <span>{t(`console.models.health.unknown.${props.healthUnknown}`)}</span>
        </p>
      ) : null}
      {props.listDenied ? <ListDeniedNote /> : null}
      {!canEnable ? (
        <p id={`${switchId}-why`} className="text-xs text-fg-muted">
          {c.accountApplied
            ? t("console.models.enableNeedsProbe")
            : t("console.models.enableNeedsPerimeter")}
        </p>
      ) : null}
      {probe.isPending ? (
        <p role="status" className="text-xs text-fg-muted">
          {t("console.models.probing")}
        </p>
      ) : null}
      {confirmLogging ? (
        <div className="flex flex-col gap-2 rounded-lg border border-warning-line bg-warning-bg p-3 text-sm text-warning-fg">
          <p>
            {c.loggingStatus === "on"
              ? t("console.models.logging.confirm")
              : t("console.models.logging.confirmUnknown")}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => toggle(true)}>
              {t("console.models.logging.enableAnyway")}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirmLogging(false)}>
              {t("console.models.logging.keepOff")}
            </Button>
          </div>
        </div>
      ) : null}
      {error ? <Notice tone="danger">{refusal(error, t)}</Notice> : null}
    </li>
  );
}

const RETENTIONS: readonly RetentionMode[] = ["default", "aws_review"];

function CreateDialog(props: {
  accounts: ModelAccount[];
  onClose: () => void;
  onCreated: (id: string, setup: ModelConnectionSetup) => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const headers = useCsrfHeaders();
  const queryClient = useQueryClient();
  const [connectionId, setConnectionId] = useState("");
  const [accountId, setAccountId] = useState(props.accounts[0]?.accountId ?? "");
  const [retention, setRetention] = useState<RetentionMode>("default");
  const [label, setLabel] = useState("");
  const create = useMutation({
    mutationFn: async () =>
      ok(
        await operator["model-connections"].$post(
          { json: { id: connectionId.trim(), accountId, retention, label: label.trim() } },
          headers,
        ),
      ),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: consoleKeys.modelConnections });
      props.onCreated(created.connection.id, created.setup);
    },
  });
  const valid = /^[a-z][a-z0-9-]{1,30}$/.test(connectionId.trim()) && accountId && label.trim();
  return (
    <AppDialog
      open
      title={t("console.models.create.title")}
      description={t("console.models.create.body")}
      onClose={props.onClose}
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid && !create.isPending) {
            create.mutate();
          }
        }}
      >
        <Field id={`${id}-label`} label={t("console.models.create.label")}>
          <Input
            id={`${id}-label`}
            required
            maxLength={80}
            value={label}
            onChange={(e) => setLabel(e.currentTarget.value)}
          />
        </Field>
        <Field
          id={`${id}-id`}
          label={t("console.models.create.id")}
          help={t("console.models.create.idHelp")}
        >
          <Input
            id={`${id}-id`}
            required
            maxLength={31}
            autoCapitalize="none"
            spellCheck={false}
            value={connectionId}
            onChange={(e) => setConnectionId(e.currentTarget.value.toLowerCase())}
            aria-describedby={`${id}-id-help`}
          />
        </Field>
        <Field id={`${id}-account`} label={t("console.models.create.account")}>
          <NativeSelect
            id={`${id}-account`}
            value={accountId}
            onChange={(e) => setAccountId(e.currentTarget.value)}
          >
            {props.accounts.map((a) => (
              <option key={a.accountId} value={a.accountId}>
                {a.accountId} · {a.label}
              </option>
            ))}
          </NativeSelect>
        </Field>
        <Field
          id={`${id}-retention`}
          label={t("console.models.create.retention")}
          help={t("console.models.create.retentionHelp")}
        >
          <NativeSelect
            id={`${id}-retention`}
            value={retention}
            onChange={(e) => setRetention(e.currentTarget.value as RetentionMode)}
            aria-describedby={`${id}-retention-help`}
          >
            {RETENTIONS.map((r) => (
              <option key={r} value={r}>
                {t(`console.models.retention.${r}`)}
              </option>
            ))}
          </NativeSelect>
        </Field>
        {create.error ? <Notice tone="danger">{refusal(create.error, t)}</Notice> : null}
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="ghost" onClick={props.onClose}>
            {t("app.cancel")}
          </Button>
          <Button type="submit" disabled={!valid || create.isPending}>
            {t("console.models.create.submit")}
          </Button>
        </div>
      </form>
    </AppDialog>
  );
}

function Field(props: { id: string; label: string; help?: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={props.id}>{props.label}</Label>
      {props.children}
      {props.help ? (
        <p id={`${props.id}-help`} className="text-xs text-fg-muted">
          {props.help}
        </p>
      ) : null}
    </div>
  );
}

/** What the model account's administrator creates, to copy: the role, its trust and permissions. */
function SetupDialog(props: { id: string; setup: ModelConnectionSetup; onClose: () => void }) {
  const { t } = useTranslation();
  const { setup } = props;
  return (
    <AppDialog
      open
      title={t("console.models.setup.title", { id: props.id })}
      description={t("console.models.setup.body")}
      onClose={props.onClose}
    >
      <div className="flex flex-col gap-4">
        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
          <dt className="text-fg-muted">{t("console.models.setup.role")}</dt>
          <dd className="font-mono break-all text-fg">{setup.roleName}</dd>
          <dt className="text-fg-muted">{t("console.models.setup.externalId")}</dt>
          <dd className="font-mono break-all text-fg">{setup.externalId}</dd>
          <dt className="text-fg-muted">{t("console.models.setup.sourceIdentity")}</dt>
          <dd className="font-mono text-fg">{setup.sourceIdentity}</dd>
        </dl>
        <PolicyBlock title={t("console.models.setup.trust")} document={setup.trustPolicy} />
        <PolicyBlock
          title={t("console.models.setup.permissions")}
          document={setup.permissionsPolicy}
        />
        <p className="text-xs text-fg-muted">{t("console.models.setup.next")}</p>
        <div className="flex justify-end">
          <Button onClick={props.onClose}>{t("app.done")}</Button>
        </div>
      </div>
    </AppDialog>
  );
}

function PolicyBlock({ title, document }: { title: string; document: Record<string, unknown> }) {
  const { t } = useTranslation();
  const text = JSON.stringify(document, null, 2);
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-fg">{title}</h3>
        <Button
          size="sm"
          variant="outline"
          onClick={() =>
            void navigator.clipboard.writeText(text).then(
              () => setCopied(true),
              () => setCopied(false),
            )
          }
        >
          {copied ? t("console.models.setup.copied") : t("console.models.setup.copy")}
        </Button>
      </div>
      <pre className="max-h-48 overflow-auto rounded-lg border border-line bg-surface-sunken p-3 font-mono text-xs text-fg-secondary">
        {text}
      </pre>
    </div>
  );
}

// ── Allowlist ───────────────────────────────────────────────────────────────────────────────

function Allowlist({ overview }: { overview: ModelConnectionsOverview }) {
  const { t } = useTranslation();
  const headers = useCsrfHeaders();
  const queryClient = useQueryClient();
  const stepUp = useSecureAction();
  const opener = useRef<HTMLElement | null>(null);
  const [adding, setAdding] = useState(false);
  const [binding, setBinding] = useState<ModelAccount | null>(null);
  // What the step-up guards: the code is the same, the reason told differs.
  const [guarding, setGuarding] = useState<"add" | "bind">("add");
  const tenants = useTenants();
  const bindable = (tenants.data ?? []).filter(
    (tenant) => tenant.status === "active" || tenant.status === "suspended",
  );
  const connectionsIn = (accountId: string) =>
    overview.connections.filter((c) => c.accountId === accountId && c.enabled).length;
  // The connection's health line follows too (#423): a trust fixed then probed clears it.
  const settle = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: consoleKeys.modelConnections }),
      queryClient.invalidateQueries({ queryKey: consoleKeys.modelHealth }),
    ]);
  const remove = useMutation({
    mutationFn: async (accountId: string) =>
      ok(await operator["model-accounts"][":accountId"].$delete({ param: { accountId } }, headers)),
    onSettled: settle,
  });
  const { calls, hasPasskey } = useOperatorStepUp();
  const full = overview.capacity.used >= overview.capacity.max;
  return (
    <PageSection id="model-accounts" title={t("console.models.accounts.title")}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-prose text-sm text-fg-secondary">{t("console.models.accounts.lead")}</p>
        <Button
          variant="outline"
          disabled={full}
          onClick={(e) => {
            opener.current = e.currentTarget;
            setGuarding("add");
            setAdding(true);
          }}
        >
          <Plus aria-hidden="true" />
          {t("console.models.accounts.add")}
        </Button>
      </div>
      {overview.accounts.length === 0 ? (
        <p className="text-sm text-fg-secondary">{t("console.models.accounts.empty")}</p>
      ) : (
        <ul className="divide-y divide-line rounded-xl border border-line bg-surface-raised">
          {overview.accounts.map((a) => (
            <li key={a.accountId} className="flex items-center gap-3 px-4 py-3">
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="text-sm font-medium text-fg tabular-nums">{a.accountId}</span>
                <span className="flex flex-wrap items-center gap-x-1.5 text-xs text-fg-muted">
                  <span className="truncate">{a.label}</span>
                  <span aria-hidden="true">·</span>
                  <BindingLine account={a} />
                  <span aria-hidden="true">·</span>
                  <StatusLine
                    status={
                      a.appliedAt
                        ? {
                            icon: CircleCheck,
                            tone: "text-success-fg",
                            text: t("console.models.accounts.applied"),
                          }
                        : {
                            icon: Clock,
                            tone: "text-warning-fg",
                            text: t("console.models.accounts.pending"),
                          }
                    }
                  />
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                aria-label={t(
                  a.boundTo === "tenant"
                    ? "console.models.accounts.changeBindingOf"
                    : "console.models.accounts.bindToTenantOf",
                  { account: a.accountId },
                )}
                onClick={(e) => {
                  opener.current = e.currentTarget;
                  setGuarding("bind");
                  setBinding(a);
                }}
              >
                {a.boundTo === "tenant"
                  ? t("console.models.accounts.changeBinding")
                  : t("console.models.accounts.bindToTenant")}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-danger-fg"
                disabled={remove.isPending}
                aria-label={t("console.models.accounts.removeOf", { account: a.accountId })}
                onClick={() => remove.mutate(a.accountId)}
              >
                {t("console.models.accounts.remove")}
              </Button>
            </li>
          ))}
        </ul>
      )}
      <p className="text-xs text-fg-muted tabular-nums">
        {t("console.models.accounts.capacity", {
          used: overview.capacity.used,
          max: overview.capacity.max,
        })}
      </p>
      {remove.error ? <Notice tone="danger">{refusal(remove.error, t)}</Notice> : null}
      {adding ? (
        <AddAccountDialog
          tenants={bindable}
          secure={stepUp.secure}
          onClose={() => setAdding(false)}
          returnFocus={() => opener.current}
        />
      ) : null}
      {binding ? (
        <BindAccountDialog
          account={binding}
          tenants={bindable}
          connections={connectionsIn(binding.accountId)}
          secure={stepUp.secure}
          onClose={() => setBinding(null)}
          returnFocus={() => opener.current}
        />
      ) : null}
      <StepUpFrame
        open={stepUp.asking}
        onClose={stepUp.cancel}
        description={
          guarding === "bind"
            ? t("console.models.accounts.bindStepUp")
            : t("console.models.accounts.stepUp")
        }
        returnFocus={() => opener.current}
      >
        <StepUpForm
          calls={calls}
          offerPasskey={hasPasskey}
          passkeyFirst
          onClose={stepUp.cancel}
          onDone={() => {
            const action = stepUp.take();
            if (action) {
              void stepUp.secure(action);
            }
          }}
        />
      </StepUpFrame>
    </PageSection>
  );
}

/**
 * Whom an account serves: the instance, or one tenant by name; no one once its tenant's purge
 * unbound it (#406), until an operator rebinds it.
 */
function BindingLine({ account }: { account: ModelAccount }) {
  const { t } = useTranslation();
  if (account.boundTo === "tenant" && account.unboundAt) {
    return (
      <span className="inline-flex items-center gap-1.5 text-warning-fg">
        <CircleAlert aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
        {t("console.models.accounts.unbound", { name: account.tenant?.name ?? "" })}
      </span>
    );
  }
  return account.boundTo === "tenant" ? (
    <span className="inline-flex items-center gap-1.5 text-fg-secondary">
      <Building2 aria-hidden="true" className="size-3.5 text-fg-muted" strokeWidth={1.75} />
      {t("console.models.accounts.boundTenant", { name: account.tenant?.name ?? "" })}
    </span>
  ) : (
    <span className="text-fg-secondary">{t("console.models.accounts.boundInstance")}</span>
  );
}

/** The binding select's value: `instance`, or a tenant's ID. */
const INSTANCE = "instance";

function toBinding(value: string): ModelAccountBinding {
  return value === INSTANCE ? { boundTo: "instance" } : { boundTo: "tenant", tenantId: value };
}

function BindingSelect(props: {
  id: string;
  value: string;
  tenants: readonly OperatorTenant[];
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <NativeSelect
      id={props.id}
      value={props.value}
      aria-describedby={`${props.id}-help`}
      onChange={(e) => props.onChange(e.currentTarget.value)}
    >
      <option value={INSTANCE}>{t("console.models.binding.instance")}</option>
      <optgroup label={t("console.models.binding.tenants")}>
        {props.tenants.map((tenant) => (
          <option key={tenant.id} value={tenant.id}>
            {tenant.name}
          </option>
        ))}
      </optgroup>
    </NativeSelect>
  );
}

/**
 * Binds an account to the instance or to one tenant (model-picking D2): a fresh authenticator
 * code, every operator told, and every connection registered in it disabled.
 */
function BindAccountDialog(props: {
  account: ModelAccount;
  tenants: readonly OperatorTenant[];
  /** Connections registered in the account: each is disabled by the change. */
  connections: number;
  secure: (action: () => Promise<void>) => Promise<void>;
  onClose: () => void;
  returnFocus: () => HTMLElement | null;
}) {
  const { t } = useTranslation();
  const id = useId();
  const headers = useCsrfHeaders();
  const queryClient = useQueryClient();
  const currentValue =
    props.account.boundTo === "tenant" ? (props.account.tenant?.id ?? INSTANCE) : INSTANCE;
  const [value, setValue] = useState(
    props.account.boundTo === "tenant" ? INSTANCE : (props.tenants[0]?.id ?? INSTANCE),
  );
  const [error, setError] = useState<Error | null>(null);
  const [busy, setBusy] = useState(false);
  const bind = async () => {
    setBusy(true);
    setError(null);
    try {
      await ok(
        await operator["model-accounts"][":accountId"].$patch(
          { param: { accountId: props.account.accountId }, json: { binding: toBinding(value) } },
          headers,
        ),
      );
      await queryClient.invalidateQueries({ queryKey: consoleKeys.modelConnections });
      props.onClose();
    } catch (e) {
      if (needsReauth(e)) {
        throw e;
      }
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AppDialog
      open
      title={t("console.models.binding.title", { account: props.account.accountId })}
      description={t("console.models.binding.body")}
      onClose={props.onClose}
      returnFocus={props.returnFocus}
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (value !== currentValue && !busy) {
            void props.secure(bind);
          }
        }}
      >
        <Field
          id={`${id}-binding`}
          label={t("console.models.binding.for")}
          help={t("console.models.binding.help")}
        >
          <BindingSelect
            id={`${id}-binding`}
            value={value}
            tenants={props.tenants}
            onChange={setValue}
          />
        </Field>
        {props.connections > 0 ? (
          <Notice tone="warning">
            {t("console.models.binding.disables", { count: props.connections })}
          </Notice>
        ) : null}
        {error ? <Notice tone="danger">{refusal(error, t)}</Notice> : null}
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="ghost" onClick={props.onClose}>
            {t("app.cancel")}
          </Button>
          <Button type="submit" disabled={value === currentValue || busy}>
            {t("console.models.binding.submit")}
          </Button>
        </div>
      </form>
    </AppDialog>
  );
}

function AddAccountDialog(props: {
  tenants: readonly OperatorTenant[];
  secure: (action: () => Promise<void>) => Promise<void>;
  onClose: () => void;
  returnFocus: () => HTMLElement | null;
}) {
  const { t } = useTranslation();
  const id = useId();
  const headers = useCsrfHeaders();
  const queryClient = useQueryClient();
  const [accountId, setAccountId] = useState("");
  const [label, setLabel] = useState("");
  const [bindTo, setBindTo] = useState(INSTANCE);
  const [error, setError] = useState<Error | null>(null);
  const [busy, setBusy] = useState(false);
  const valid = /^\d{12}$/.test(accountId) && label.trim().length > 0;
  // A refusal for want of a fresh code goes back to `secure`, which asks for one and runs this
  // again; any other failure stays in the dialog.
  const add = async () => {
    setBusy(true);
    setError(null);
    try {
      await ok(
        await operator["model-accounts"].$post(
          {
            json: {
              accountId,
              label: label.trim(),
              // Only a tenant binding is sent: the previous release refuses the key (canary).
              ...(bindTo !== INSTANCE && { binding: toBinding(bindTo) }),
            },
          },
          headers,
        ),
      );
      await queryClient.invalidateQueries({ queryKey: consoleKeys.modelConnections });
      props.onClose();
    } catch (e) {
      if (needsReauth(e)) {
        throw e;
      }
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AppDialog
      open
      title={t("console.models.addAccount.title")}
      description={t("console.models.addAccount.body")}
      onClose={props.onClose}
      returnFocus={props.returnFocus}
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid && !busy) {
            void props.secure(add);
          }
        }}
      >
        <Field id={`${id}-account`} label={t("console.models.addAccount.account")}>
          <Input
            id={`${id}-account`}
            required
            inputMode="numeric"
            maxLength={12}
            className="tabular-nums"
            value={accountId}
            onChange={(e) => setAccountId(e.currentTarget.value.replace(/\D/g, ""))}
          />
        </Field>
        <Field id={`${id}-label`} label={t("console.models.addAccount.label")}>
          <Input
            id={`${id}-label`}
            required
            maxLength={80}
            value={label}
            onChange={(e) => setLabel(e.currentTarget.value)}
          />
        </Field>
        <Field
          id={`${id}-binding`}
          label={t("console.models.binding.for")}
          help={t("console.models.binding.help")}
        >
          <BindingSelect
            id={`${id}-binding`}
            value={bindTo}
            tenants={props.tenants}
            onChange={setBindTo}
          />
        </Field>
        <Notice tone="warning">{t("console.models.addAccount.widens")}</Notice>
        {error ? <Notice tone="danger">{refusal(error, t)}</Notice> : null}
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="ghost" onClick={props.onClose}>
            {t("app.cancel")}
          </Button>
          <Button type="submit" disabled={!valid || busy}>
            {t("console.models.addAccount.submit")}
          </Button>
        </div>
      </form>
    </AppDialog>
  );
}
